import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { ALL_WESTERN_ZODIACS, zodiacSlug } from "@/lib/calendar/zodiac";
import { computePair, pairSlug } from "@/lib/calendar/zodiac-compat";
import { withSlash } from "@/lib/url";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/zodiak/cocok/")({
  head: () => {
    const title = "Kecocokan Zodiak: Kalkulator Cocok-Cocokan 12 Bintang";
    const desc = "Cek kecocokan antar zodiak — pilih dua bintang untuk membaca skor cinta, persahabatan, dan kerja. Lengkap untuk 78 pasangan zodiak unik.";
    const canonical = "https://hari.co.id/zodiak/cocok/";
    return {
      meta: [
        { title }, { name: "description", content: desc },
        { property: "og:title", content: title }, { property: "og:description", content: desc },
        { property: "og:url", content: canonical },
      ],
      links: [{ rel: "canonical", href: canonical }],
    };
  },
  component: ZodiakCocokIndex,
});

function ZodiakCocokIndex() {
  const [el, setEl] = useState<"Semua" | "Api" | "Tanah" | "Udara" | "Air">("Semua");
  const filtered = ALL_WESTERN_ZODIACS.filter((z) => el === "Semua" || z.elementId === el);
  const popular: Array<[string, string]> = [
    ["leo", "sagittarius"], ["cancer", "pisces"],
    ["taurus", "capricorn"], ["aries", "libra"],
    ["gemini", "aquarius"], ["scorpio", "pisces"],
  ];
  return (
    <SiteShell>
      <div className="mx-auto max-w-5xl py-10">
        <nav className="mb-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-primary">Beranda</Link> /{" "}
          <Link to={withSlash("/zodiak") as never} className="hover:text-primary">Zodiak</Link> / Kecocokan
        </nav>
        <h1 className="font-display text-4xl tracking-tight md:text-5xl">Kecocokan Zodiak</h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          Pilih dua zodiak untuk melihat skor kecocokan cinta, persahabatan, dan kerja.
          Total 78 kombinasi unik tersedia berbasis elemen, modalitas, dan tradisi astrologi.
        </p>

        <section className="mt-6">
          <h2 className="text-lg font-semibold">Pasangan Populer</h2>
          <div className="mt-3 grid gap-2 sm:grid-cols-2 md:grid-cols-3">
            {popular.map(([a, b]) => {
              const za = ALL_WESTERN_ZODIACS.find((z) => zodiacSlug(z) === a)!;
              const zb = ALL_WESTERN_ZODIACS.find((z) => zodiacSlug(z) === b)!;
              const s = computePair(za, zb).overall;
              return (
                <Link
                  key={`${a}-${b}`}
                  to={withSlash(`/zodiak/cocok/${pairSlug(za, zb)}`) as never}
                  className="rounded-lg border border-border bg-card p-3 hover:border-primary"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{za.symbol} {za.nameId} & {zb.symbol} {zb.nameId}</span>
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary">{s}%</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        <div className="mt-8 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase text-muted-foreground">Filter elemen:</span>
          {(["Semua", "Api", "Tanah", "Udara", "Air"] as const).map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => setEl(e)}
              className={cn(
                "rounded-full px-3 py-1 text-xs",
                el === e ? "bg-primary text-primary-foreground" : "border border-border hover:bg-accent"
              )}
            >
              {e}
            </button>
          ))}
        </div>

        <div className="mt-8 overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-center text-xs">
            <thead className="bg-muted/50">
              <tr>
                <th className="p-2"></th>
                {filtered.map((z) => (
                  <th key={z.id} className="p-2">
                    <Link to={withSlash(`/zodiak/${zodiacSlug(z)}`) as never} className="hover:text-primary">
                      <div className="text-base">{z.symbol}</div>
                      <div className="text-[10px]">{z.nameId.slice(0, 4)}</div>
                    </Link>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id} className="border-t border-border">
                  <th className="bg-muted/30 p-2 text-left">
                    <Link to={withSlash(`/zodiak/${zodiacSlug(a)}`) as never} className="hover:text-primary">
                      <span className="mr-1">{a.symbol}</span>{a.nameId.slice(0, 4)}
                    </Link>
                  </th>
                  {filtered.map((b) => {
                    const s = computePair(a, b).overall;
                    const tone = s >= 80 ? "bg-green-500/20 text-green-700 dark:text-green-300"
                      : s >= 65 ? "bg-yellow-500/20 text-yellow-700 dark:text-yellow-300"
                      : "bg-red-500/15 text-red-700 dark:text-red-300";
                    return (
                      <td key={b.id} className="p-0">
                        <Link
                          to={withSlash(`/zodiak/cocok/${pairSlug(a, b)}`) as never}
                          className={`block p-2 font-semibold hover:opacity-80 ${tone}`}
                          title={`${a.nameId} & ${b.nameId}: ${s}%`}
                        >
                          {s}
                        </Link>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-4 text-xs text-muted-foreground">
          Skor merupakan referensi astrologi populer berbasis elemen, modalitas, dan tradisi kompatibilitas. Bukan ramalan deterministik. URL non-kanonik (mis. <code>/zodiak/cocok/leo-dan-aries/</code>) otomatis di-redirect ke versi kanonik (<code>/zodiak/cocok/aries-dan-leo/</code>).
        </p>
      </div>
    </SiteShell>
  );
}